/**
 * Articles Admin API Service
 *
 * API client for admin article CRUD operations.
 */

import { apiClient } from './client';
import type { Article, ArticleMeta } from '../../pages/learn/types';

const BASE_PATH = '/admin/articles';
const PUBLIC_PATH = '/articles';

// ─── Response Types ─────────────────────────────────────

export interface ArticleListResponse {
  success: boolean;
  articles: (ArticleMeta & { createdAt: string; updatedAt: string })[];
}

export interface ArticleResponse {
  success: boolean;
  article: Article & { createdAt: string; updatedAt: string };
}

export interface SeedResponse {
  success: boolean;
  created: number;
  skipped: number;
}

// ─── Public API (no auth required) ──────────────────────

export const publicArticlesApi = {
  list(category?: string): Promise<ArticleListResponse> {
    const params = category ? `?category=${category}` : '';
    return apiClient.get<ArticleListResponse>(`${PUBLIC_PATH}${params}`);
  },

  get(slug: string): Promise<ArticleResponse> {
    return apiClient.get<ArticleResponse>(`${PUBLIC_PATH}/${slug}`);
  },
};

// ─── Admin API (auth required) ──────────────────────────

export const articlesApi = {
  list(): Promise<ArticleListResponse> {
    return apiClient.get<ArticleListResponse>(BASE_PATH);
  },

  get(slug: string): Promise<ArticleResponse> {
    return apiClient.get<ArticleResponse>(`${BASE_PATH}/${slug}`);
  },

  create(article: Article): Promise<ArticleResponse> {
    return apiClient.post<ArticleResponse>(BASE_PATH, article);
  },

  update(slug: string, data: Partial<Article>): Promise<ArticleResponse> {
    return apiClient.put<ArticleResponse>(`${BASE_PATH}/${slug}`, data);
  },

  delete(slug: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`${BASE_PATH}/${slug}`);
  },

  seed(articles: Article[]): Promise<SeedResponse> {
    return apiClient.post<SeedResponse>(`${BASE_PATH}/seed`, { articles });
  },
};
